import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import RestoreIcon from '@mui/icons-material/Restore'
import { Link, router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'
import { Markdown } from '@/components/Markdown'
import { useSortable } from '@/hooks/useSortable'

interface Project {
  id: number
  name: string
  slug: string
  description: string | null
  member_count: number
  board_count: number
  notebook_count: number
  webhook_count: number
  my_role: string | null
  archived: boolean
  archived_at: string | null
  created_at: string
}

type StatusFilter = 'active' | 'archived'

interface Props {
  projects: Project[]
  status: StatusFilter
  counts: { active: number, archived: number }
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  member: 'Member',
}

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'board_count', label: 'Boards' },
  { value: 'notebook_count', label: 'Notebooks' },
  { value: 'webhook_count', label: 'Webhooks' },
  { value: 'member_count', label: 'Members' },
  { value: 'created_at', label: 'Created' },
]

export default function Index({ projects, status, counts }: Props) {
  const { sorted, order, orderBy, handleSort } = useSortable(projects, 'name')

  function truncateText(text: string, maxLen: number): string {
    if (text.length <= maxLen) return text
    return text.slice(0, maxLen).trimEnd() + '...'
  }

  function handleStatusChange(_: React.SyntheticEvent, newStatus: StatusFilter) {
    router.get('/manage/projects', { status: newStatus }, { preserveState: false, preserveScroll: false })
  }

  function handleRestore(e: React.MouseEvent, project: Project) {
    e.preventDefault()
    e.stopPropagation()
    router.patch(`/manage/projects/${project.id}/unarchive`)
  }

  const isArchivedView = status === 'archived'

  return (
    <SidebarLayout
      section="manage"
      title="Projects"
      action={
        <Button component={Link} href="/manage/projects/new" variant="contained">
          New project
        </Button>
      }
    >
      <Tabs
        value={status}
        onChange={handleStatusChange}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="active" label={`Active (${counts.active})`} />
        <Tab value="archived" label={`Archived (${counts.archived})`} />
      </Tabs>

      {projects.length > 1 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={orderBy}
              label="Sort by"
              onChange={e => handleSort(e.target.value)}
            >
              {SORT_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton size="small" onClick={() => handleSort(orderBy)}>
            {order === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          </IconButton>
        </Box>
      )}

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          {isArchivedView
            ? 'No archived projects.'
            : 'No projects found. Create one to get started.'}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {sorted.map((project) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={project.id}>
              <Paper
                component={Link}
                href={`/manage/projects/${project.id}`}
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  textDecoration: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s',
                  opacity: project.archived ? 0.7 : 1,
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1, mr: 1 }}>
                    {project.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    {project.archived && (
                      <Chip label="Archived" size="small" color="default" />
                    )}
                    {project.my_role && (
                      <Chip label={ROLE_LABELS[project.my_role] || project.my_role} size="small" variant="outlined" />
                    )}
                  </Box>
                </Box>

                {project.description ? (
                  <Tooltip
                    title={<Markdown variant="body2">{project.description}</Markdown>}
                    arrow
                    placement="bottom-start"
                    slotProps={{ tooltip: { sx: { maxWidth: 400, p: 1.5, bgcolor: 'background.paper', color: 'text.primary', border: '1px solid', borderColor: 'divider' } } }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {truncateText(project.description.replace(/[#*_`~\[\]()>|\\-]/g, ''), 80)}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>—</Typography>
                )}

                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                  <Chip label={`${project.board_count} boards`} size="small" variant="outlined" />
                  <Chip label={`${project.notebook_count} notebooks`} size="small" variant="outlined" />
                  <Chip label={`${project.webhook_count} webhooks`} size="small" variant="outlined" />
                  <Chip label={`${project.member_count} members`} size="small" variant="outlined" />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary">
                    {project.archived && project.archived_at
                      ? `Archived ${new Date(project.archived_at).toLocaleDateString()}`
                      : `Created ${new Date(project.created_at).toLocaleDateString()}`}
                  </Typography>
                  {project.archived && (
                    <Button
                      size="small"
                      startIcon={<RestoreIcon fontSize="small" />}
                      onClick={(e) => handleRestore(e, project)}
                    >
                      Restore
                    </Button>
                  )}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

    </SidebarLayout>
  )
}
